import React, { useEffect, useState } from "react";
import PopupDialog from "components/dialog/PopupDialog";
import useAxiosFunction from "hooks/useAxiosFunction";
import { showEmpty } from "features/kms/system/SystemPageUtils";

function statusBadgeClass(statusCode) {
  switch (statusCode) {
    case "DIAJUKAN":
      return "badge badge-primary";
    case "PERLU_PERBAIKAN":
      return "badge badge-warning";
    case "TERVERIFIKASI":
    case "TERVERIFIKASI_CABOR":
    case "TERVERIFIKASI_KONI_DAERAH":
    case "TERVERIFIKASI_KONI_PROVINSI":
      return "badge badge-success";
    case "DITOLAK":
      return "badge badge-danger";
    default:
      return "badge badge-secondary";
  }
}

function statusHelpText(statusCode) {
  switch (String(statusCode || "").toUpperCase()) {
    case "DRAFT":
      return "Data masih draft dan belum masuk antrian verifikasi.";
    case "DIAJUKAN":
      return "Data sudah diajukan dan sedang menunggu review verifier.";
    case "PERLU_PERBAIKAN":
      return "Data perlu diperbaiki sebelum diajukan kembali.";
    case "DITOLAK":
      return "Data ditolak dan perlu ditinjau ulang oleh operator.";
    default:
      return "Data sudah masuk tahap verifikasi lanjutan atau final.";
  }
}

function FieldView({ label, children }) {
  return (
    <div className="col-md-6 mb-3">
      <strong>{label}</strong>
      <div className="text-muted mt-1">{children}</div>
    </div>
  );
}

export default function OrganizationVerificationDialog({
  show,
  organizationId,
  organizationName,
  canManage,
  closeHandle,
  onSubmitted,
}) {
  const [detailResp, detailError, detailLoading, fetchDetail] = useAxiosFunction();
  const [historyResp, historyError, historyLoading, fetchHistory] = useAxiosFunction();
  const [, submitError, submitLoading, submitVerification] = useAxiosFunction();
  const [catatan, setCatatan] = useState("");

  useEffect(() => {
    if (!show || !organizationId) return;
    fetchDetail({ method: "GET", url: `/organisasi/${organizationId}/verification`, auth: true });
    fetchHistory({ method: "GET", url: `/organisasi/${organizationId}/verification/history?page=1`, auth: true });
    setCatatan("");
  }, [show, organizationId]);

  const detail = detailResp?.data || {};
  const histories = Array.isArray(historyResp?.data) ? historyResp.data : [];
  const canSubmit = canManage && detail?.can_submit;

  const doSubmit = async () => {
    const resp = await submitVerification({
      method: "POST",
      url: `/organisasi/${organizationId}/verification/submit`,
      auth: true,
      data: { catatan },
    });

    if (resp?.data?.success === true) {
      onSubmitted?.();
      closeHandle?.();
    }
  };

  const statusCode = detail?.status_code || "DRAFT";
  const statusName = detail?.status_name || "Draft";
  const footerLabel = submitLoading ? "Mengajukan..." : "Ajukan Verifikasi";

  return (
    <PopupDialog
      title={`Verifikasi Organisasi${organizationName ? ` - ${organizationName}` : ""}`}
      size="xl"
      show={show}
      closeHandle={closeHandle}
      allowClose={true}
      closeLabel="Tutup"
      doHandle={canSubmit ? doSubmit : null}
      doLabel={footerLabel}
      doDisabled={submitLoading}
    >
      {detailLoading ? <div>Memuat data verifikasi...</div> : null}
      {!detailLoading && detailError ? <div className="alert alert-danger">Gagal memuat status verifikasi: {detailError}</div> : null}
      {!detailLoading && !detailError ? (
        <>
          <div className="alert alert-light border mb-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between">
              <div className="mb-2 mb-md-0">
                <div className="text-muted">Status Saat Ini</div>
                <div>
                  <span className={statusBadgeClass(statusCode)}>{statusName}</span>
                </div>
              </div>
              <div className="text-muted">{statusHelpText(statusCode)}</div>
            </div>
          </div>

          <div className="row">
            <FieldView label="Status">
              <span className={statusBadgeClass(statusCode)}>{statusName}</span>
            </FieldView>
            <FieldView label="Diajukan">
              {showEmpty(detail?.submitted_at)}
            </FieldView>
            <FieldView label="Diverifikasi">
              {showEmpty(detail?.verified_at)}
            </FieldView>
            <FieldView label="Pengaju">
              {showEmpty(detail?.submitted_by_username)}
            </FieldView>
            <FieldView label="Verifier">
              {showEmpty(detail?.verified_by_username)}
            </FieldView>
            <div className="col-md-12 mb-3">
              <strong>Catatan Terakhir</strong>
              <div className="text-muted mt-1 p-2 border rounded bg-light">
                {showEmpty(detail?.catatan)}
              </div>
            </div>
          </div>

          {canSubmit ? (
            <div className="card card-outline card-primary mt-2">
              <div className="card-header py-2">
                <h3 className="card-title" style={{ float: "none" }}>Pengajuan Verifikasi</h3>
              </div>
              <div className="card-body">
                <label htmlFor="verification-catatan">
                  <strong>Catatan Pengajuan</strong>
                </label>
                <textarea
                  id="verification-catatan"
                  className="form-control"
                  rows={4}
                  value={catatan}
                  onChange={(event) => setCatatan(event.target.value)}
                  placeholder="Isi catatan pengajuan verifikasi bila diperlukan"
                />
                <small className="text-muted d-block mt-2">
                  Aksi pengajuan tersedia saat status masih Draft atau Perlu Perbaikan.
                </small>
              </div>
            </div>
          ) : (
            <div className="alert alert-info mt-3 mb-0">
              {canManage ? "Status saat ini tidak dapat diajukan ulang." : "User aktif hanya memiliki akses baca terhadap verifikasi organisasi ini."}
            </div>
          )}

          {submitError ? <div className="alert alert-danger mt-3 mb-0">Gagal mengajukan verifikasi: {submitError}</div> : null}
        </>
      ) : null}

      <hr />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h5 className="mb-0">Riwayat Verifikasi</h5>
        <small className="text-muted">{histories.length > 0 ? `${histories.length} riwayat` : "Belum ada riwayat"}</small>
      </div>
      {historyLoading ? <div>Memuat riwayat verifikasi...</div> : null}
      {!historyLoading && historyError ? <div className="alert alert-danger">Gagal memuat riwayat verifikasi: {historyError}</div> : null}
      {!historyLoading && !historyError && histories.length < 1 ? <div>Tidak ada riwayat verifikasi.</div> : null}
      {!historyLoading && !historyError && histories.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-bordered table-sm table-striped">
            <thead>
              <tr>
                <th style={{ width: "50px" }}>No</th>
                <th style={{ width: "120px" }}>Aksi</th>
                <th style={{ width: "220px" }}>Perubahan Status</th>
                <th style={{ width: "150px" }}>Actor</th>
                <th style={{ width: "180px" }}>Waktu</th>
                <th>Catatan</th>
              </tr>
            </thead>
            <tbody>
              {histories.map((item, index) => (
                <tr key={item.id_riwayat_verifikasi || `${organizationId}-${index}`}>
                  <td className="text-center">{index + 1}</td>
                  <td>{showEmpty(item?.action)}</td>
                  <td>
                    <div>{showEmpty(item?.old_status_name, "Status awal")}</div>
                    <div className="text-muted">
                      <i className="fa fa-arrow-right" /> {showEmpty(item?.new_status_name)}
                    </div>
                  </td>
                  <td>{showEmpty(item?.actor_username)}</td>
                  <td>{showEmpty(item?.created_at)}</td>
                  <td>{showEmpty(item?.catatan)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </PopupDialog>
  );
}
